import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Music2,
  PenLine,
  Piano,
  Sparkles,
  Clock,
  Gift,
  ShieldCheck,
  TrendingUp,
  Heart,
  Award,
  CheckCircle2,
  PlayCircle,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { LOGO_URL, BEISPIELSONGS_URL } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1591025810539-a321000cda85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwc2hlZXQlMjBtdXNpYyUyMHNvZnQlMjBsaWdodHxlbnwwfHx8fDE3ODM1Mjc1MjZ8MA&ixlib=rb-4.1.0&q=85";

const fade = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.09, ease: "easeOut" },
  }),
};

const viewFade = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const mehrwert = [
  { icon: TrendingUp, text: "Erweitern Sie Ihr Dienstleistungsangebot." },
  { icon: Award, text: "Heben Sie sich von anderen Bestattungsunternehmen ab." },
  { icon: Heart, text: "Bieten Sie Angehörigen eine einzigartige und emotionale Erinnerung." },
  { icon: Gift, text: "Zusätzlicher Umsatz durch eine gefragte, hochwertige Zusatzleistung." },
  { icon: CheckCircle2, text: "Canto Dei übernimmt den kompletten Produktionsprozess für Sie." },
  { icon: Clock, text: "Bestellung in wenigen Minuten." },
  { icon: Music2, text: "Lieferung in der Regel innerhalb von 24 Stunden." },
  { icon: ShieldCheck, text: "Jeder Songtext und jede Songkomposition werden individuell geprüft." },
];

const leistungen = [
  { icon: PenLine, text: "Persönliche Songtexte" },
  { icon: Piano, text: "Individuelle Musikkomposition" },
  { icon: Sparkles, text: "Studioqualität" },
  { icon: ShieldCheck, text: "Sorgfältige Qualitätskontrolle" },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-cream">
      <header className="sticky top-0 z-40 bg-cream/95 backdrop-blur border-b border-sand">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <Logo size={40} withText />
          <nav className="flex items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-charcoal hover:text-gold px-3 sm:px-4" data-testid="nav-login-button">
                Login
              </Button>
            </Link>
            <Link to="/registrieren">
              <Button className="bg-gold hover:bg-golddark text-white rounded-sm px-4 sm:px-5 transition-colors duration-500" data-testid="nav-register-button">
                Registrieren
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-cream/70" />
        </div>
        <div className="relative max-w-4xl mx-auto px-5 sm:px-6 py-20 sm:py-32 text-center">
          <motion.div variants={fade} initial="hidden" animate="show" custom={0} className="flex justify-center">
            <img
              src={LOGO_URL}
              alt="Canto Dei"
              width={160}
              height={160}
              className="w-32 h-32 sm:w-40 sm:h-40 object-contain"
              data-testid="hero-logo"
            />
          </motion.div>
          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={1}
            className="mt-6 font-serif text-3xl sm:text-4xl font-light text-charcoal tracking-tight"
          >
            Canto Dei
          </motion.p>
          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-3 text-xs sm:text-sm text-gold tracking-[0.2em] uppercase"
          >
            Individuelle personalisierte Trauersongs
          </motion.p>
          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-10 sm:mt-12 font-serif italic text-2xl sm:text-4xl text-charcoal/85 max-w-3xl mx-auto leading-snug"
          >
            „Wenn Worte fehlen, spricht die Musik – und sie erzählt von dir.“
          </motion.p>
          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-8 sm:mt-10 text-base sm:text-lg text-charcoal/70 max-w-2xl mx-auto leading-relaxed"
          >
            Canto Dei ermöglicht Bestattungsunternehmen, ihren Kunden einen individuellen
            personalisierten Trauersong anzubieten – als würdevolle Erinnerung an einen
            geliebten Menschen. So erweitern Sie Ihr Angebot um eine Dienstleistung,
            die berührt und Sie von Mitbewerbern abhebt.
          </motion.p>
          <motion.div
            variants={fade}
            initial="hidden"
            animate="show"
            custom={5}
            className="mt-10 sm:mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/registrieren" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-gold hover:bg-golddark text-white rounded-sm px-10 sm:px-12 py-6 sm:py-7 text-base transition-colors duration-500" data-testid="hero-register-button">
                Registrieren
              </Button>
            </Link>
            <Link to="/login" className="w-full sm:w-auto">
              <Button
                variant="outline"
                className="w-full sm:w-auto border-gold text-gold hover:bg-gold hover:text-white rounded-sm px-10 sm:px-12 py-6 sm:py-7 text-base transition-colors duration-500"
                data-testid="hero-login-button"
              >
                Login
              </Button>
            </Link>
          </motion.div>
          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={6}
            className="mt-6 inline-flex items-center gap-2 text-sm sm:text-base text-gold"
            data-testid="hero-free-hint"
          >
            <Gift className="w-4 h-4" strokeWidth={1.5} />
            Der erste individuelle personalisierte Trauersong ist für registrierte Kunden kostenlos.
          </motion.p>
        </div>
      </section>

      {/* QUALITÄTSVERSPRECHEN — prominent & weit oben */}
      <section className="bg-charcoal text-cream">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 py-20 sm:py-28 text-center">
          <motion.div variants={viewFade} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }}>
            <span className="inline-flex w-14 h-14 sm:w-16 sm:h-16 rounded-full border border-gold/40 items-center justify-center mb-6 sm:mb-8">
              <ShieldCheck className="w-6 h-6 sm:w-7 sm:h-7 text-gold" strokeWidth={1.5} />
            </span>
            <p className="text-xs tracking-[0.3em] uppercase text-gold">Unser Qualitätsversprechen</p>
            <p className="mt-6 sm:mt-8 font-serif text-2xl sm:text-4xl font-light leading-snug max-w-3xl mx-auto">
              Jeder Songtext und jede Songkomposition werden von unseren Musiktechnikern
              individuell geprüft, angepasst und bearbeitet.
            </p>
            <div className="mt-10 sm:mt-12 grid gap-5 sm:gap-6 sm:grid-cols-2 max-w-2xl mx-auto text-left">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-sm text-cream/85 leading-relaxed">
                  Kein Song wird automatisch oder ungeprüft ausgeliefert.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-sm text-cream/85 leading-relaxed">
                  Jeder Auftrag wird vor der Auslieferung persönlich geprüft.
                </p>
              </div>
            </div>
            <p className="mt-8 sm:mt-10 text-sm text-cream/60 max-w-2xl mx-auto leading-relaxed">
              So stellen wir sicher, dass jeder individuelle personalisierte Trauersong
              höchsten qualitativen Ansprüchen gerecht wird.
            </p>
          </motion.div>
        </div>
      </section>

      {/* WARUM CANTO DEI */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 py-20 sm:py-28">
        <motion.div variants={viewFade} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} className="max-w-2xl">
          <p className="text-xs tracking-[0.3em] uppercase text-gold">Für Bestattungsunternehmen</p>
          <h2 className="mt-4 font-serif text-3xl sm:text-5xl font-light text-charcoal">
            Warum Canto Dei?
          </h2>
          <p className="mt-6 text-base sm:text-lg font-serif italic text-gold leading-snug">
            Sie verkaufen keinen Song. Sie bieten Ihren Kunden eine neue Form des Abschieds.
          </p>
          <p className="mt-4 text-sm text-charcoal/65 leading-relaxed">
            Diese Dienstleistung ist in dieser Form derzeit nahezu einzigartig im deutschsprachigen Raum.
          </p>
        </motion.div>

        <div className="mt-12 sm:mt-16 grid gap-x-12 gap-y-8 sm:gap-y-10 sm:grid-cols-2">
          {mehrwert.map(({ icon: Icon, text }, i) => (
            <motion.div
              key={i}
              variants={viewFade}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: i * 0.05 }}
              className="flex items-start gap-4"
              data-testid={`warum-${i}`}
            >
              <span className="shrink-0 w-11 h-11 rounded-sm border border-sand flex items-center justify-center bg-white">
                <Icon className="w-5 h-5 text-gold" strokeWidth={1.5} />
              </span>
              <p className="text-base text-charcoal/85 leading-relaxed pt-2">{text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BEISPIELSONGS */}
      <section className="bg-white border-y border-sand">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-20 sm:py-28 text-center">
          <motion.div variants={viewFade} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}>
            <h2 className="font-serif text-3xl sm:text-5xl font-light text-charcoal">Beispielsongs</h2>
            <div className="mt-8 space-y-5 text-base text-charcoal/70 leading-relaxed text-left sm:text-center">
              <p>
                Die hier präsentierten Beispielsongs wurden bewusst mit einer ähnlichen Struktur
                und Stimmung komponiert, um einen möglichst direkten und fairen Vergleich der
                verschiedenen Musikstile zu ermöglichen.
              </p>
              <p>
                So können Sie besser nachvollziehen, wie unterschiedlich ein persönlicher
                Trauersong je nach gewähltem Stil wirken kann.
              </p>
              <p>
                Jeder finale Song wird individuell auf die Persönlichkeit, die Erinnerungen und
                die gewünschte emotionale Atmosphäre abgestimmt und dadurch zu einem einzigartigen
                musikalischen Abschied.
              </p>
            </div>
            <a href={BEISPIELSONGS_URL} target="_blank" rel="noopener noreferrer" className="inline-block mt-10">
              <Button
                variant="outline"
                className="border-gold text-gold hover:bg-gold hover:text-white rounded-sm px-8 sm:px-10 py-6 text-base gap-2 transition-colors duration-500"
                data-testid="beispielsongs-button"
              >
                <PlayCircle className="w-5 h-5" strokeWidth={1.5} />
                Beispielsongs anhören
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* LEISTUNGEN */}
      <section className="max-w-5xl mx-auto px-5 sm:px-6 py-20 sm:py-24">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {leistungen.map(({ icon: Icon, text }, i) => (
            <motion.div
              key={i}
              variants={viewFade}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: i * 0.08 }}
              className="text-center"
              data-testid={`leistung-${i}`}
            >
              <span className="inline-flex w-12 h-12 rounded-full border border-sand items-center justify-center bg-white mb-4">
                <Icon className="w-5 h-5 text-gold" strokeWidth={1.5} />
              </span>
              <p className="text-sm text-charcoal/80">{text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* KOSTENLOS TESTEN / CTA */}
      <section className="bg-white border-t border-sand">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-20 sm:py-28 text-center">
          <motion.div variants={viewFade} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}>
            <h2 className="font-serif text-3xl sm:text-5xl font-light text-charcoal">
              Kostenlos testen
            </h2>
            <p className="mt-8 text-base sm:text-lg text-charcoal/70 leading-relaxed">
              Der erste individuelle personalisierte Trauersong ist für registrierte
              Kunden kostenlos. Registrieren Sie Ihr Bestattungsunternehmen und erweitern
              Sie Ihr Angebot um einen Service, der berührt.
            </p>
            <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/registrieren" className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto bg-gold hover:bg-golddark text-white rounded-sm px-10 sm:px-12 py-6 sm:py-7 text-base transition-colors duration-500" data-testid="cta-register-button">
                  Registrieren
                </Button>
              </Link>
              <Link to="/login" className="w-full sm:w-auto">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto border-gold text-gold hover:bg-gold hover:text-white rounded-sm px-10 sm:px-12 py-6 sm:py-7 text-base transition-colors duration-500"
                  data-testid="cta-login-button"
                >
                  Login
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
